import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useAuth } from "@/hooks/useAuth";
import { Sidebar } from "@/components/dashboard/Sidebar";
import { ReferralModal } from "@/components/referrals/ReferralModal";
import { JobDetailsHeader } from "@/components/jobs/JobDetailsHeader";
import { JobInfoCard } from "@/components/jobs/JobInfoCard";
import { JobSidebar } from "@/components/jobs/JobSidebar";
import { JobDetailsSkeleton } from "@/components/jobs/JobDetailsSkeleton";
import { JobNotFound } from "@/components/jobs/JobNotFound";
import { Job } from "@/lib/schema";
import { getJob } from "@/api/jobs";
import { COMMON_CLASSES } from "@/lib/jobConstants";
import { trackEvent } from "@/lib/firebase";

export default function JobDetails() {
  const { id } = useParams<{ id: string }>();
  const [_, setLocation] = useLocation();
  const { currentUser } = useAuth();
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [referralModalOpen, setReferralModalOpen] = useState(false);

  const { data: job, isLoading, error } = useQuery({
    queryKey: ["/api/jobs", id],
    queryFn: () => getJob(id!),
    enabled: !!id,
  });

  // Track job view when job is loaded
  useEffect(() => {
    if (job && currentUser) {
      trackEvent('view_job', {
        job_id: job.id,
        job_title: job.title,
        job_category: job.category,
        user_id: currentUser.uid,
      });
    }
  }, [job, currentUser]);

  const handleBackToJobs = () => {
    setLocation("/jobs");
  };

  const handleReferSomeone = () => {
    if (!currentUser || !job) return;
    setSelectedJob(job);
    setReferralModalOpen(true);
  };

  if (isLoading) {
    return (
      <ProtectedRoute>
        <div className={COMMON_CLASSES.layout.container}>
          <Sidebar />
          <div className={COMMON_CLASSES.layout.content}>
            <JobDetailsSkeleton />
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (error || !job) {
    return (
      <ProtectedRoute>
        <div className={COMMON_CLASSES.layout.container}>
          <Sidebar />
          <div className={COMMON_CLASSES.layout.content}>
            <JobNotFound onBack={handleBackToJobs} />
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="min-h-screen page-gradient">
        <Sidebar />
        <div className={`${COMMON_CLASSES.layout.content} relative z-10`}>
          <div className="max-w-7xl mx-auto">
            <JobDetailsHeader onBack={handleBackToJobs} />
            
            <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-8 xl:gap-10">
              <div className="lg:col-span-2 xl:col-span-3 order-2 lg:order-1">
                <JobInfoCard job={job} />
              </div>
              <div className="lg:col-span-1 xl:col-span-1 order-1 lg:order-2">
                <div className="lg:sticky lg:top-8">
                  <JobSidebar job={job} onReferSomeone={handleReferSomeone} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ReferralModal
        open={referralModalOpen}
        onOpenChange={setReferralModalOpen}
        job={selectedJob}
      />
    </ProtectedRoute>
  );
}